// 房間分組(V4P-5,自 ls-v4 myhome 房間視覺化移植)。
//
// v1=本地偏好(localStorage,per-uid key)——與 tile 拖拉排序同一個 §9-G3
// 「本地排序偏好」模式;伺服端同步(跨裝置)列缺口,落地時走 user-prefs
// additive 面、不動 devices 契約。devices 表沒有 room 欄=刻意(平台規格
// 不動;房間是純呈現層概念)。

import i18n from '../../i18n';

const keyFor = (uid: string) => `lsdg.rooms.${uid || 'anon'}`;
// boot 競速:silent refresh 後 user 物件晚到(AuthContext 註),uid 短暫為
// ''——last-written 鏡像讓 reload 不丟分組;uid 到位後讀回同一份。
const LAST_KEY = 'lsdg.rooms.last';

// 「未分房」分組的穩定內部鍵(用於 localStorage 比對 / data-testid / dedup,
// 不可隨語言變)。i18n:顯示文案改用 unassignedRoomLabel() 即時取,確保切語言重算。
export const UNASSIGNED_ROOM = ' unassigned';

/** 「未分房」分組的顯示文案(i18n,使用點才求值)。 */
export function unassignedRoomLabel(): string {
  return i18n.t('devices.unassignedRoom');
}

export type RoomMap = Record<string, string>; // device_id -> room name

export function loadRooms(uid: string): RoomMap {
  try {
    const raw = localStorage.getItem(keyFor(uid)) ?? localStorage.getItem(LAST_KEY);
    if (!raw) return {};
    const v = JSON.parse(raw) as unknown;
    return v && typeof v === 'object' ? (v as RoomMap) : {};
  } catch {
    return {};
  }
}

export function saveRoom(uid: string, deviceId: string, room: string): RoomMap {
  const map = loadRooms(uid);
  const trimmed = room.trim();
  // 空字串、內部哨兵、或使用者輸入當前語言的「未分房」字樣 → 視為清除分組。
  if (trimmed === '' || trimmed === UNASSIGNED_ROOM || trimmed === unassignedRoomLabel()) delete map[deviceId];
  else map[deviceId] = trimmed;
  try {
    const enc = JSON.stringify(map);
    localStorage.setItem(keyFor(uid), enc);
    localStorage.setItem(LAST_KEY, enc);
  } catch {
    /* quota/private mode — degrade to in-memory for this session */
  }
  return map;
}

/**
 * 分組:已命名房間按字典序,未分房恆排最後;房間內維持原列表順序。
 * `room`=穩定鍵(自訂房名 or UNASSIGNED_ROOM 哨兵,供 key / data-testid);
 * `label`=顯示文案(未分房走 i18n,自訂房名原樣)。
 */
export function groupByRoom<T extends { device_id: string }>(devices: T[], map: RoomMap): Array<{ room: string; label: string; devices: T[] }> {
  const rooms = new Map<string, T[]>();
  for (const d of devices) {
    const room = map[d.device_id] ?? UNASSIGNED_ROOM;
    const arr = rooms.get(room) ?? [];
    arr.push(d);
    rooms.set(room, arr);
  }
  const named = Array.from(rooms.keys()).filter((r) => r !== UNASSIGNED_ROOM).sort((a, b) => a.localeCompare(b, 'zh-Hant'));
  const out: Array<{ room: string; label: string; devices: T[] }> = named.map((room) => ({ room, label: room, devices: rooms.get(room)! }));
  const un = rooms.get(UNASSIGNED_ROOM);
  if (un) out.push({ room: UNASSIGNED_ROOM, label: unassignedRoomLabel(), devices: un });
  return out;
}
